import { useCallback, useRef, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../contexts/AuthContext';
import { readClientDevice } from './deviceClass';

type TrackingAction =
  | 'view'
  | 'favorite'
  | 'scroll-profile'
  | 'scroll-pitch-deck'
  | 'click-website'
  | 'click-link'
  | 'next-match';

interface TrackInteractionOptions {
  action: TrackingAction;
  startupId: string;
  duration?: number; // Para 'view' - tiempo en milisegundos
  source?: 'catalog' | 'match' | 'search' | 'recommendation' | 'favorites';
  category?: string; // Ej: "Your Matches", "Women Led"
  metadata?: {
    scrollDepth?: number; // 0-100 para scroll actions
    elementClicked?: string; // Para click-link
    linkUrl?: string; // Para click-website y click-link
    sessionId?: string;
  };
}

/**
 * Hook personalizado para trackear interacciones del usuario
 *
 * @example
 * const { trackInteraction, trackView, trackScroll } = useTracking();
 *
 * // Trackear view cuando se monta el componente
 * useEffect(() => {
 *   const startTime = Date.now();
 *   trackView(startupId, { source: 'catalog', category: 'Your Matches' });
 *
 *   return () => {
 *     const duration = Date.now() - startTime;
 *     // El view se trackea con duración al desmontar
 *   };
 * }, [startupId]);
 */
export const useTracking = () => {
  const { user, firebaseUser } = useAuth();
  const functions = getFunctions();
  const viewStartTimeRef = useRef<number | null>(null);

  /**
   * Función genérica para trackear cualquier interacción
   */
  const trackInteraction = useCallback(
    async (options: TrackInteractionOptions) => {
      // Solo trackear si el usuario está autenticado y es investor
      if (!firebaseUser || !user || user.userType !== 'investor') {
        // console.log('[Tracking] Skipped - User not authenticated or not investor');
        return;
      }

      try {
        const { device, viewportWidth } = readClientDevice();
        const trackInteractionFn = httpsCallable(functions, 'trackInteraction');
        await trackInteractionFn({
          userId: user.id,
          startupId: options.startupId,
          action: options.action,
          duration: options.duration,
          source: options.source,
          category: options.category,
          metadata: options.metadata,
          device,
          viewportWidth,
        });
      } catch (error) {
        // No queremos que errores de tracking rompan la experiencia del usuario
        console.error('[Tracking] Error tracking interaction:', error);
      }
    },
    [user, firebaseUser, functions]
  );

  /**
   * Inicia el tracking de un "view"
   * Debe llamarse cuando el componente se monta
   * Solo guarda el tiempo de inicio, NO trackea todavía
   */
  const startViewTracking = useCallback(
    (startupId: string, options?: { source?: string; category?: string }) => {
      if (!firebaseUser || !user || user.userType !== 'investor') {
        return;
      }

      // Solo guardar el tiempo de inicio
      viewStartTimeRef.current = Date.now();
    },
    [user, firebaseUser]
  );

  /**
   * Finaliza el tracking de un "view" y envía la duración total
   * Debe llamarse cuando el componente se desmonta o cuando el usuario navega
   */
  const endViewTracking = useCallback(
    (startupId: string, options?: { source?: string; category?: string }) => {
      if (!viewStartTimeRef.current) return;

      const duration = Date.now() - viewStartTimeRef.current;
      viewStartTimeRef.current = null;

      // Trackear el view con la duración total (solo una vez, al final)
      trackInteraction({
        action: 'view',
        startupId,
        duration,
        source: options?.source as any,
        category: options?.category,
      });
    },
    [trackInteraction]
  );

  /**
   * Trackear scroll en el perfil
   */
  const trackScrollProfile = useCallback(
    (startupId: string, scrollDepth: number) => {
      trackInteraction({
        action: 'scroll-profile',
        startupId,
        metadata: { scrollDepth },
      });
    },
    [trackInteraction]
  );

  /**
   * Trackear scroll en el pitch deck
   */
  const trackScrollPitchDeck = useCallback(
    (startupId: string, scrollDepth: number) => {
      trackInteraction({
        action: 'scroll-pitch-deck',
        startupId,
        metadata: { scrollDepth },
      });
    },
    [trackInteraction]
  );

  /**
   * Trackear click en website
   */
  const trackClickWebsite = useCallback(
    (startupId: string, url: string) => {
      trackInteraction({
        action: 'click-website',
        startupId,
        metadata: { linkUrl: url },
      });
    },
    [trackInteraction]
  );

  /**
   * Trackear click en cualquier otro link
   */
  const trackClickLink = useCallback(
    (startupId: string, url: string, elementClicked?: string) => {
      trackInteraction({
        action: 'click-link',
        startupId,
        metadata: {
          linkUrl: url,
          elementClicked,
        },
      });
    },
    [trackInteraction]
  );

  /**
   * Trackear agregar a favoritos
   */
  const trackFavorite = useCallback(
    (startupId: string, options?: { source?: string; category?: string }) => {
      trackInteraction({
        action: 'favorite',
        startupId,
        source: options?.source as any,
        category: options?.category,
      });
    },
    [trackInteraction]
  );

  /**
   * Trackear "next match" (pasar al siguiente)
   */
  const trackNextMatch = useCallback(
    (startupId: string, options?: { source?: string }) => {
      trackInteraction({
        action: 'next-match',
        startupId,
        source: options?.source as any,
      });
    },
    [trackInteraction]
  );

  return {
    trackInteraction,
    startViewTracking,
    endViewTracking,
    trackScrollProfile,
    trackScrollPitchDeck,
    trackClickWebsite,
    trackClickLink,
    trackFavorite,
    trackNextMatch,
  };
};
